import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { PaywallProvider } from './PaywallContext';
import Login from './components/Login';
import LandingPageWhiteOrange from './components/LandingPageWhiteOrange';
import Seo from './components/Seo';
import Legal, { LegalPage } from './components/Legal';
import { BLOG_POSTS, faqJsonLd } from './components/BlogPage';
import { getSimulatorModels, updateProfile, getWalletBalance, captureWalletTopup, deductWallet, trackAffiliateClick, getSubscriptionStatus, getMemorySettings, updateMemorySettings, getMemorySummary, getBringYourOwnModelSettings, updateBringYourOwnModelSettings,
  whatsappLink } from './api';
import PhonePromptModal from './components/PhonePromptModal';
import { DEFAULT_MODEL, ALLOWED_MODELS, NOVA_CANVAS_MODEL, NOVA_CANVAS_LABEL } from './modelPolicy';

// ── Route-level code splitting ────────────────────────────────────────────────
// Everything below is reached only after the visitor commits (a landing-page
// click, a login, a nav tab). Loading it eagerly put ~800KB of JS and the
// whole stylesheet on the landing page's critical path; as lazy chunks each
// screen pays only for itself and the shared CSS shrinks to what the first
// paint needs.
const LandingPage = lazy(() => import('./components/LandingPage'));
const LandingPageLight = lazy(() => import('./components/LandingPageLight'));
const LandingPageGold = lazy(() => import('./components/LandingPageGold'));
const LandingPageOrange = lazy(() => import('./components/LandingPageOrange'));
const HomePage = lazy(() => import('./components/HomePage'));
const NewLandingPage = lazy(() => import('./components/NewLandingPage'));
const LandingPageMain = lazy(() => import('./components/LandingPageMain'));
const BlogIndexPage = lazy(() => import('./components/BlogPage').then((m) => ({ default: m.BlogIndexPage })));
const BlogPostPage = lazy(() => import('./components/BlogPage').then((m) => ({ default: m.BlogPostPage })));
const Chat = lazy(() => import('./components/Chat'));
const Courses = lazy(() => import('./components/Courses'));
const Shop = lazy(() => import('./components/Shop'));
const Wallet = lazy(() => import('./components/Wallet').then((m) => ({ default: m.Wallet })));
const ReferEarn = lazy(() => import('./components/Wallet').then((m) => ({ default: m.ReferEarn })));
const Affiliate = lazy(() => import('./components/Affiliate'));
const GoodGPTAdmin = lazy(() => import('./components/GoodGPTAdmin'));
const AccessGate = lazy(() => import('./components/AccessGate'));
const ModelAdmin = lazy(() => import('./components/ModelAdmin'));
const ConsultantPortal = lazy(() => import('./components/ConsultantPortal'));
const NoteView = lazy(() => import('./components/NoteView'));
const Presentation = lazy(() => import('./components/Presentation'));
const Connectors = lazy(() => import('./components/Connectors'));
const PlansPage = lazy(() => import('./components/PlansPage'));
const Profile = lazy(() => import('./components/Profile'));
const LiteratureSearch = lazy(() => import('./components/LiteratureSearch'));
const AITutor = lazy(() => import('./components/AITutor'));
const EmailCalendarAgent = lazy(() => import('./components/EmailCalendarAgent'));
const SEOAgent = lazy(() => import('./components/SEOAgent'));
const CareerQuest = lazy(() => import('./components/CareerQuest'));
const PublishHub = lazy(() => import('./components/PublishHub'));
const Settings = lazy(() => import('./components/Settings'));
const Assets = lazy(() => import('./components/Assets'));

// Occupies the content box while a chunk arrives, so swapping in the real
// screen shifts nothing (the shell around it never unmounts).
function RouteFallback() {
  return <div className="route-fallback" aria-busy="true" />;
}

const TABS = [
  { key: 'chat',         label: 'Chat',         icon: '💬', path: '/chat' },
  { key: 'search',       label: 'Discover',     icon: '🔍', path: '/search' },
  { key: 'tutor',        label: 'AI Advisor',   icon: '🪄', path: '/tutor' },
  // { key: 'coach',        label: 'Coach',        icon: '🎯', path: '/coach' },
  { key: 'courses',      label: 'Courses',      icon: '🎓', path: '/courses' },
  { key: 'publish',      label: 'Publish',      icon: '📝', path: '/publish' },
  { key: 'assets',     label: 'Assets',       icon: '🗂️', path: '/assets' },
  { key: 'boardroom', label: 'Boardroom', icon: '⚔️', path: '/career-quest' },
  { key: 'presentation', label: 'Presentation', icon: '🖥️', path: '/presentation' },
  { key: 'connectors',          label: 'Integrations', icon: '🔌', path: '/connectors' },
];

const PUBLISHING_TABS = [
  { key: 'shop',         label: 'Shop',         icon: '🛒', path: '/shop' },
];

const ALL_TABS = [...TABS, ...PUBLISHING_TABS];

const TAB_BY_PATH = Object.fromEntries(ALL_TABS.map((t) => [t.path, t]));
TAB_BY_PATH['/white-paper'] = PUBLISHING_TABS[0];
TAB_BY_PATH['/publish-book'] = PUBLISHING_TABS[0];
TAB_BY_PATH['/patent'] = PUBLISHING_TABS[0];
const SITE_URL = 'https://goodgpt.site';
const BLOG_INDEX_URL = `${SITE_URL}/blog`;
const APP_PLAY_URL = 'https://play.google.com/store/apps/details?id=site.goodgpt.app';

function hasAdminAccess(user) {
  return !!(
    user?.is_admin ||
    user?.isAdmin ||
    user?.admin ||
    user?.is_superuser ||
    user?.superuser ||
    user?.role === 'admin' ||
    user?.role === 'superuser'
  );
}

function seoPage({ title, description, canonical, noindex = false, keywords, jsonLd, ogType = 'website' }) {
  return {
    title,
    description,
    canonical,
    noindex,
    keywords,
    jsonLd,
    ogType,
  };
}

const HOME_SEO = seoPage({
  title: 'GoodGPT Empowering people to do better, every day',
  description: 'GoodGPT is a persistent-memory AI assistant for chat, research, email, calendar, presentations, tutoring, and productivity workflows. Free to start.',
  canonical: `${SITE_URL}/`,
  keywords: 'AI assistant, persistent memory, productivity, literature search, email agent, presentation builder, AI tutor, GoodGPT Android app, mobile AI assistant',
  jsonLd: {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'GoodGPT',
        url: SITE_URL,
        description: 'GoodGPT is a persistent-memory AI assistant for chat, research, email, calendar, presentations, tutoring, and productivity workflows.',
        applicationCategory: 'ProductivityApplication',
        operatingSystem: 'Web',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        featureList: [
          'Persistent memory',
          'Literature discovery',
          'Email and calendar agent',
          'Presentation builder',
          'AI tutor',
          'Multi-model support',
        ],
      },
      {
        '@type': 'MobileApplication',
        name: 'GoodGPT',
        description: 'GoodGPT for Android is a persistent-memory AI assistant for chat, research, email, calendar, presentations, tutoring, and productivity workflows.',
        operatingSystem: 'Android',
        applicationCategory: 'ProductivityApplication',
        downloadUrl: APP_PLAY_URL,
        installUrl: APP_PLAY_URL,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        publisher: { '@type': 'Organization', name: 'GoodGPT', url: SITE_URL },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            'name': 'Is GoodGPT free to use?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Yes — you can sign up and start using GoodGPT at no cost. A free plan gives you access to core features including smart chat, memory, and literature search.',
            },
          },
          {
            '@type': 'Question',
            'name': 'How is GoodGPT different from ChatGPT?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'GoodGPT is built around persistent memory — it learns your goals, preferences, and communication style over time so you never have to repeat yourself.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Does GoodGPT really remember my conversations?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Yes. GoodGPT uses a semantic memory system that stores important context from your conversations and recalls it intelligently when it is relevant — even weeks later.',
            },
          },
        ],
      },
    ],
  },
});

const PUBLIC_ROUTE_SEO = {
  '/blog': seoPage({
    title: 'GoodGPT Blog - Feature Guides for AI Search and Productivity',
    description: 'Explore GoodGPT feature guides on persistent memory, research, email, smart chat, AI Discuss, Boardroom, presentations, and the Chrome extension.',
    canonical: BLOG_INDEX_URL,
    keywords: 'GoodGPT blog, AI feature guides, persistent memory, research assistant, email agent, presentation builder, Chrome extension',
    ogImageAlt: 'GoodGPT Blog feature guides',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'GoodGPT Blog',
      url: BLOG_INDEX_URL,
      description: 'Feature guides for GoodGPT covering persistent memory, research, email, smart chat, AI Discuss, Boardroom, presentations, and the Chrome extension.',
      hasPart: BLOG_POSTS.map((post) => ({ '@type': 'BlogPosting', name: post.title, url: `${SITE_URL}${post.path}` })),
    },
  }),
  '/plans': seoPage({
    title: 'GoodGPT Plans - Start Free and Upgrade as You Grow',
    description: 'Explore GoodGPT plans and pricing. Start free, then upgrade for higher usage limits and premium productivity features.',
    canonical: `${SITE_URL}/plans`,
    ogImageAlt: 'GoodGPT plans and pricing',
  }),
  '/privacy': seoPage({
    title: 'GoodGPT Privacy Policy',
    description: 'Read the GoodGPT privacy policy for details on how account data, memory, and AI interactions are collected and used.',
    canonical: `${SITE_URL}/privacy`,
    ogImageAlt: 'GoodGPT privacy policy',
  }),
  '/terms': seoPage({
    title: 'GoodGPT Terms of Service',
    description: 'Read the GoodGPT terms of service covering subscriptions, use of the AI assistant, and Chrome extension usage.',
    canonical: `${SITE_URL}/terms`,
    ogImageAlt: 'GoodGPT terms of service',
  }),
  '/consultants': seoPage({
    title: 'GoodGPT Consultants',
    description: 'Browse GoodGPT consultants and book expert help for focused guidance and sessions.',
    canonical: `${SITE_URL}/consultants`,
    ogImageAlt: 'GoodGPT consultants',
  }),
};

const APP_ROUTE_SEO = {
  '/search': seoPage({
    title: 'GoodGPT Discover - AI Literature Search',
    description: 'Search books, literature, and quality sources with GoodGPT Discover.',
    canonical: `${SITE_URL}/search`,
    noindex: true,
    ogImageAlt: 'GoodGPT Discover search',
  }),
  '/profile': seoPage({
    title: 'GoodGPT Profile',
    description: 'Manage your GoodGPT account details, memory, and personal preferences.',
    canonical: `${SITE_URL}/profile`,
    noindex: true,
    ogImageAlt: 'GoodGPT profile',
  }),
  '/tutor': seoPage({
    title: 'GoodGPT AI Advisor',
    description: 'Practice communication, leadership, interviews, and coaching with GoodGPT AI Advisor.',
    canonical: `${SITE_URL}/tutor`,
    noindex: true,
    ogImageAlt: 'GoodGPT AI Advisor',
  }),
  // '/coach': seoPage({
  //   title: 'GoodGPT Executive Coach',
  //   description: 'Rehearse the executive conversation you keep putting off: bad news, difficult peers, and board pressure.',
  //   canonical: `${SITE_URL}/coach`,
  //   noindex: true,
  //   ogImageAlt: 'GoodGPT Executive Coach',
  // }),
  '/chat': seoPage({
    title: 'GoodGPT Chat',
    description: 'Chat with GoodGPT for drafting, brainstorming, analysis, and quick answers.',
    canonical: `${SITE_URL}/chat`,
    noindex: true,
    ogImageAlt: 'GoodGPT chat',
  }),
  '/courses': seoPage({
    title: 'GoodGPT Courses',
    description: 'Browse GoodGPT courses and learning modules.',
    canonical: `${SITE_URL}/courses`,
    noindex: true,
    ogImageAlt: 'GoodGPT courses',
  }),
  '/shop': seoPage({
    title: 'GoodGPT Shop',
    description: 'Buy GoodGPT products and services.',
    canonical: `${SITE_URL}/shop`,
    noindex: true,
    ogImageAlt: 'GoodGPT shop',
  }),
  '/affiliate': seoPage({
    title: 'GoodGPT Affiliate',
    description: 'Manage your GoodGPT affiliate links and referrals.',
    canonical: `${SITE_URL}/affiliate`,
    noindex: true,
    ogImageAlt: 'GoodGPT affiliate program',
  }),
  '/career-quest': seoPage({
    title: 'GoodGPT Boardroom',
    description: 'Build workplace skills with AI-guided missions and feedback.',
    canonical: `${SITE_URL}/career-quest`,
    noindex: true,
    ogImageAlt: 'GoodGPT Boardroom',
  }),
  '/presentation': seoPage({
    title: 'GoodGPT Presentation Builder',
    description: 'Create slide decks and presentation outlines with GoodGPT.',
    canonical: `${SITE_URL}/presentation`,
    noindex: true,
    ogImageAlt: 'GoodGPT presentation builder',
  }),
  '/email-agent': seoPage({
    title: 'GoodGPT Email Agent',
    description: 'Draft replies and manage scheduling with GoodGPT Email Agent.',
    canonical: `${SITE_URL}/email-agent`,
    noindex: true,
    ogImageAlt: 'GoodGPT email agent',
  }),
  '/publish': seoPage({
    title: 'GoodGPT Publishing Studio',
    description: 'Publish white papers, books, and patents with GoodGPT AI.',
    canonical: `${SITE_URL}/publish`,
    noindex: true,
    ogImageAlt: 'GoodGPT publishing studio',
  }),
  '/white-paper': seoPage({
    title: 'GoodGPT White Paper Publisher',
    description: 'Write and format white papers with GoodGPT.',
    canonical: `${SITE_URL}/white-paper`,
    noindex: true,
    ogImageAlt: 'GoodGPT white paper publisher',
  }),
  '/patent': seoPage({
    title: 'GoodGPT Patent Publisher',
    description: 'Draft and format patent applications with GoodGPT.',
    canonical: `${SITE_URL}/patent`,
    noindex: true,
    ogImageAlt: 'GoodGPT patent publisher',
  }),
  '/connectors': seoPage({
    title: 'GoodGPT Connectors',
    description: 'Connect GoodGPT with your tools and integrations.',
    canonical: `${SITE_URL}/connectors`,
    noindex: true,
    ogImageAlt: 'GoodGPT connectors',
  }),
  '/plans-page': seoPage({
    title: 'GoodGPT Subscription',
    description: 'Review GoodGPT subscription plans and billing.',
    canonical: `${SITE_URL}/plans-page`,
    noindex: true,
    ogImageAlt: 'GoodGPT subscription plans',
  }),
  '/seo': seoPage({
    title: 'GoodGPT SEO Agent',
    description: 'Generate SEO content, keywords, and blog support with GoodGPT.',
    canonical: `${SITE_URL}/seo`,
    noindex: true,
    ogImageAlt: 'GoodGPT SEO agent',
  }),
  '/settings': seoPage({
    title: 'GoodGPT Settings',
    description: 'Adjust GoodGPT memory, privacy, and account preferences.',
    canonical: `${SITE_URL}/settings`,
    noindex: true,
    ogImageAlt: 'GoodGPT settings',
  }),
};

function getSeoConfig(pathname, pathParts, isAuthed, showLanding) {
  const landingVariants = new Set(['/lp', '/landingpage', '/lpgold', '/lpvirtualassistantimage', '/orangelandingpage', '/redlandingpage', '/memorylandingpage']);
  const blogPost = BLOG_POSTS.find((post) => post.path === pathname);

  if (blogPost) {
    return {
      title: `${blogPost.metaTitle} | GoodGPT`,
      description: blogPost.description,
      canonical: `${SITE_URL}${blogPost.path}`,
      keywords: blogPost.keywords,
      ogType: 'article',
      ogImageAlt: blogPost.metaTitle,
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'BlogPosting',
            headline: blogPost.metaTitle,
            description: blogPost.description,
            mainEntityOfPage: `${SITE_URL}${blogPost.path}`,
            url: `${SITE_URL}${blogPost.path}`,
            author: { '@type': 'Organization', name: 'GoodGPT' },
            publisher: {
              '@type': 'Organization',
              name: 'GoodGPT',
              logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo512.png` },
            },
          },
          faqJsonLd(blogPost.faq),
        ],
      },
    };
  }

  if (pathParts.length === 2 && !['goodgptadmin', 'modeladmin', 'plans', 'consultants', 'search', 'profile', 'tutor', 'chat', 'coach', 'courses', 'create-course', 'shop', 'affiliate', 'career-quest', 'presentation', 'email-agent', 'settings', 'connectors', 'wallet', 'privacy', 'terms', 'landingpage', 'lpgold', 'lp', 'memorylandingpage', 'orangelandingpage', 'redlandingpage', 'seo', 'email-contact-graph', 'blog', 'white-paper', 'patent'].includes(pathParts[0])) {
    const [phone, noteName] = pathParts;
    const decodedName = decodeURIComponent(noteName || '');
    return seoPage({
      title: `GoodGPT Note - ${decodedName || 'Shared Note'}`,
      description: decodedName
        ? `Read the shared GoodGPT note titled ${decodedName}.`
        : 'Read a shared GoodGPT note on GoodGPT.',
      canonical: `${SITE_URL}/${phone}/${noteName}`,
      noindex: true,
      ogType: 'article',
      ogImageAlt: decodedName || 'GoodGPT shared note',
    });
  }

  if (!isAuthed && pathname === '/' && showLanding) return HOME_SEO;
  if (pathname === '/') {
    return seoPage({
      title: 'GoodGPT App',
      description: 'Log in to use GoodGPT chat, research, memory, and productivity tools.',
      canonical: SITE_URL,
      noindex: true,
      ogImageAlt: 'GoodGPT app login',
    });
  }

  if (PUBLIC_ROUTE_SEO[pathname]) return PUBLIC_ROUTE_SEO[pathname];
  if (APP_ROUTE_SEO[pathname]) return APP_ROUTE_SEO[pathname];
  if (landingVariants.has(pathname)) {
    return seoPage({
      title: 'GoodGPT Empowering people to do better, every day',
      description: 'GoodGPT combines persistent memory, email management, research, and coaching in one place.',
      canonical: `${SITE_URL}${pathname}`,
      noindex: false,
      ogImageAlt: 'GoodGPT landing page',
    });
  }

  return seoPage({
    title: 'GoodGPT App',
    description: 'GoodGPT is a persistent-memory AI assistant for chat, research, and productivity workflows.',
    canonical: SITE_URL,
    noindex: true,
    ogImageAlt: 'GoodGPT app',
  });
}

// ── Conversation helpers ──────────────────────────────────────────────────────
const STORAGE_KEY = 'goodgpt_conversations';
function loadConversations() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
  catch { return []; }
}

function saveConversations(convs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(convs));
}

function newConversation() {
  return { id: crypto.randomUUID(), title: 'New chat', messages: [], createdAt: Date.now() };
}

// ── Embedded (React Native WebView) mode ─────────────────────────────────────
// Contract for the native shell: open any page with ?embedded=1 (also accepts
// ?app=1 / ?webview=1) to get the chromeless mobile view. Without the param,
// embedded WebViews are detected via the RN JS bridge or platform UA signals.
// Detection persists in sessionStorage so in-app routing keeps the mode.
const EMBEDDED_KEY = 'goodgpt_embedded';
function detectEmbeddedView() {
  try {
    const q = new URLSearchParams(window.location.search);
    const p = (q.get('embedded') || q.get('app') || q.get('webview') || '').toLowerCase();
    if (p === '1' || p === 'true' || p === 'yes' || p === 'native') {
      try { sessionStorage.setItem(EMBEDDED_KEY, '1'); } catch {}
      return true;
    }
  } catch {}
  try { if (sessionStorage.getItem(EMBEDDED_KEY) === '1') return true; } catch {}
  try { if (window.ReactNativeWebView) return true; } catch {}
  try {
    const ua = navigator.userAgent || '';
    if (/; wv\)/i.test(ua)) return true; // Android WebView
    // iOS WKWebView sends a Safari UA without the Version/ token
    if (/iPhone|iPad|iPod/i.test(ua) && /AppleWebKit/i.test(ua) && /Safari\//i.test(ua) && !/Version\//i.test(ua)) return true;
  } catch {}
  return false;
}

// Post a JSON message to the native shell (no-op in browsers).
function notifyHost(message) {
  try { window.ReactNativeWebView?.postMessage(JSON.stringify(message)); } catch {}
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [auth, setAuth] = useState(null);
  const [showLanding, setShowLanding] = useState(true);
  const [loginMode, setLoginMode] = useState(null);
  // Embedded WebView mode: chromeless mobile view driven by the native shell.
  const [embedded] = useState(() => detectEmbeddedView());
  const [navOpen, setNavOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [legalTab, setLegalTab] = useState(null);
  const [profileForm, setProfileForm] = useState({ name: '', mobile_number: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const [conversations, setConversations] = useState(loadConversations);
  // Conversations are stored newest-first, so index 0 is the last one the user
  // was in. Reopen it on load — otherwise a refresh drops the user into an
  // empty composer while their history sits untouched in the sidebar.
  const [activeConvId, setActiveConvId] = useState(() => loadConversations()[0]?.id ?? null);

  const [allModels, setAllModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  // When Chat's Image Gen tab is active, lock model to Amazon Nova Canvas
  const [imageGenLocked, setImageGenLocked] = useState(false);
  const handleImageGenLockedChange = useCallback((locked) => setImageGenLocked(locked), []);

  const [wallet, setWallet]               = useState({ balance: 0, transactions: [] });
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletOpen, setWalletOpen]       = useState(false);
  const [referOpen, setReferOpen]         = useState(false);

  const [activeSub, setActiveSub] = useState(null);
  // Access policy from /usersapp/subscription/status/ — accessMode decides
  // whether the app is usable without a plan, trial drives the banner.
  const [accessMode, setAccessMode] = useState(null);
  const [accessTrial, setAccessTrial] = useState(null);
  const [accessChecked, setAccessChecked] = useState(false);

  const [memoryEnabled, setMemoryEnabled] = useState(true);
  const [memoryCount, setMemoryCount] = useState(0);

  const [skipDatabaseSave, setSkipDatabaseSave] = useState(false);

  const [bringYourOwnModelForm, setBringYourOwnModelForm] = useState({
    bring_your_own_model_enabled: false,
    bring_your_own_model_provider: '',
    bring_your_own_model_model_id: '',
    bring_your_own_model_api_key: '',
    bring_your_own_model_endpoint_url: '',
    bring_your_own_model_aws_region: '',
    bring_your_own_model_api_key_set: false,
  });
  const [bringYourOwnModelSaving, setBringYourOwnModelSaving] = useState(false);
  const [bringYourOwnModelError, setBringYourOwnModelError] = useState('');
  const [bringYourOwnModelSuccess, setBringYourOwnModelSuccess] = useState('');

  const [showPhoneModal, setShowPhoneModal] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('goodgpt_auth');
    if (saved) {
      try {
        const authData = JSON.parse(saved);
        setAuth(authData);
        if (!authData?.user?.mobile_number) setShowPhoneModal(true);
        return;
      } catch { localStorage.removeItem('goodgpt_auth'); }
    }
    // Firebase backend-down fallback sessions are persisted in the same
    // localStorage key by handleLogin, so no extra restore step is needed.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Embedded WebView bridge ──────────────────────────────────────────────
  useEffect(() => {
    document.body.classList.toggle('embedded-view', embedded);
    return () => document.body.classList.remove('embedded-view');
  }, [embedded]);

  // Keep the native shell in sync with client-side routing.
  useEffect(() => {
    if (embedded) notifyHost({ type: 'route', path: location.pathname });
  }, [embedded, location.pathname]);

  // Let the native shell inject auth: post {type:'goodgpt-auth', auth:{user, token}}.
  useEffect(() => {
    const onHostMessage = (e) => {
      let data = null;
      try { data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data; } catch { return; }
      if (data?.type === 'goodgpt-auth' && data.auth?.user && typeof data.auth.token === 'string' && data.auth.token) {
        const authData = { user: data.auth.user, token: data.auth.token };
        try { localStorage.setItem('goodgpt_auth', JSON.stringify(authData)); } catch {}
        setAuth(authData);
      }
    };
    window.addEventListener('message', onHostMessage);
    document.addEventListener('message', onHostMessage); // Android WebView posts here
    return () => {
      window.removeEventListener('message', onHostMessage);
      document.removeEventListener('message', onHostMessage);
    };
  }, []);

  const refreshWallet = async (token) => {
    if (!token) { setWallet({ balance: 0, transactions: [] }); return; }
    setWalletLoading(true);
    try {
      const data = await getWalletBalance(token);
      if (data.success) {
        setWallet({ balance: data.balance ?? 0, transactions: data.transactions ?? [] });
      }
    } catch { /* keep current state on network error */ }
    finally { setWalletLoading(false); }
  };

  useEffect(() => {
    if (auth?.token) {
      refreshWallet(auth.token);
      getSubscriptionStatus(auth.token)
        .then((d) => {
          setActiveSub(d.active_subscription || null);
          setAccessMode(d.access_mode || null);
          setAccessTrial(d.trial || null);
        })
        .catch(() => {})
        .finally(() => setAccessChecked(true));
      getMemorySettings(auth.token)
        .then((d) => {
          if (d?.success) {
            setMemoryEnabled(!!d.memory_enabled);
            if (typeof d.private_mode === 'boolean') setSkipDatabaseSave(d.private_mode);
          }
        })
        .catch(() => {});
      getBringYourOwnModelSettings(auth.token)
        .then((d) => {
          if (d?.success) {
            setBringYourOwnModelForm((f) => ({
              ...f,
              bring_your_own_model_enabled: !!d.bring_your_own_model_enabled,
              bring_your_own_model_provider: d.bring_your_own_model_provider || '',
              bring_your_own_model_model_id: d.bring_your_own_model_model_id || '',
              bring_your_own_model_endpoint_url: d.bring_your_own_model_endpoint_url || '',
              bring_your_own_model_aws_region: d.bring_your_own_model_aws_region || '',
              bring_your_own_model_api_key_set: !!d.bring_your_own_model_api_key_set,
              bring_your_own_model_api_key: '',
            }));
          }
        })
        .catch(() => {});
      const email = auth.user?.email_id;
      if (email) {
        getMemorySummary(email)
          .then((d) => { if (d?.success && typeof d.memory_count === 'number') setMemoryCount(d.memory_count); })
          .catch(() => {});
      }
    } else {
      setWallet({ balance: 0, transactions: [] });
      setActiveSub(null);
      setAccessMode(null);
      setAccessTrial(null);
      setAccessChecked(false);
      setMemoryEnabled(true);
      setMemoryCount(0);
    }
  }, [auth?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMemoryToggle = async (enabled) => {
    setMemoryEnabled(enabled);
    if (auth?.token) {
      updateMemorySettings(auth.token, { memory_enabled: enabled })
        .catch(() => setMemoryEnabled(!enabled));
    }
  };

  const handlePrivateModeToggle = async (enabled) => {
    setSkipDatabaseSave(enabled);
    if (auth?.token) {
      updateMemorySettings(auth.token, { private_mode: enabled })
        .catch(() => setSkipDatabaseSave(!enabled));
    }
  };

  const handleSaveBringYourOwnModel = async () => {
    if (!auth?.token) return;
    setBringYourOwnModelSaving(true);
    setBringYourOwnModelError('');
    setBringYourOwnModelSuccess('');
    try {
      const payload = {
        bring_your_own_model_enabled:      bringYourOwnModelForm.bring_your_own_model_enabled,
        bring_your_own_model_provider:     bringYourOwnModelForm.bring_your_own_model_provider,
        bring_your_own_model_model_id:     bringYourOwnModelForm.bring_your_own_model_model_id,
        bring_your_own_model_endpoint_url: bringYourOwnModelForm.bring_your_own_model_endpoint_url,
        bring_your_own_model_aws_region:   bringYourOwnModelForm.bring_your_own_model_aws_region,
      };
      if (bringYourOwnModelForm.bring_your_own_model_api_key) {
        payload.bring_your_own_model_api_key = bringYourOwnModelForm.bring_your_own_model_api_key;
      }
      const d = await updateBringYourOwnModelSettings(auth.token, payload);
      if (d?.success) {
        setBringYourOwnModelSuccess('Bring Your Own Model settings saved.');
        setBringYourOwnModelForm((f) => ({
          ...f,
          bring_your_own_model_api_key: '',
          bring_your_own_model_api_key_set: payload.bring_your_own_model_api_key
            ? true
            : f.bring_your_own_model_api_key_set,
        }));
      } else {
        setBringYourOwnModelError(d?.message || 'Failed to save settings.');
      }
    } catch {
      setBringYourOwnModelError('Network error. Please try again.');
    } finally {
      setBringYourOwnModelSaving(false);
    }
  };

  useEffect(() => {
    const mobile = auth?.user?.email_id || '';
    const key = (base) => `${base}:${mobile || 'unknown'}`;
    const getBool = (base, fallback) => { try { const v = localStorage.getItem(key(base)); return v === null ? fallback : v === 'true'; } catch { return fallback; } };
    setSkipDatabaseSave(getBool('goodgpt_memory_skip_db', false));
  }, [auth?.user?.email_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const mobile = auth?.user?.email_id || '';
    if (!mobile) return;
    try { localStorage.setItem(`goodgpt_memory_skip_db:${mobile}`, String(skipDatabaseSave)); } catch {}
  }, [auth?.user?.email_id, skipDatabaseSave]);

  useEffect(() => {
    if (!auth?.token) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get('wallet_capture')) return;
    const paypalOrderId = params.get('token');
    window.history.replaceState({}, '', window.location.pathname);
    if (!paypalOrderId) return;
    captureWalletTopup(auth.token, paypalOrderId)
      .then((data) => {
        if (data.success) {
          setWallet({ balance: data.balance ?? 0, transactions: data.transactions ?? [] });
          navigate('/wallet');
        }
      })
      .catch(() => {});
  }, [auth?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const affCode = params.get('aff');
    if (affCode) {
      localStorage.setItem('goodgpt_aff_code', affCode);
      localStorage.setItem('goodgpt_aff_ts', Date.now().toString());
      const url = new URL(window.location.href);
      url.searchParams.delete('aff');
      window.history.replaceState({}, '', url.toString());
      trackAffiliateClick(affCode).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShopPurchase = async (amount, description) => {
    if (!auth?.token) return { success: false, message: 'Not logged in.' };
    try {
      const data = await deductWallet(auth.token, amount, description);
      if (data.success) {
        setWallet({ balance: data.balance ?? 0, transactions: data.transactions ?? wallet.transactions });
        return { success: true };
      }
      return { success: false, message: data.message || 'Payment failed.' };
    } catch {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  useEffect(() => {
    getSimulatorModels(auth?.token).then((d) => {
      const DISPLAY = [
        { id: 'gpt-6-luna', label: 'GPT-6 Luna' },
        { id: 'gpt-4o-mini', label: 'GPT-4o Mini' },
        { id: 'mistral-7b', label: 'Mistral 7B Instruct' },
        { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5' },
        { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
      ];
      // Only show the five requested models — wired for every task, remove all others
      const allowedSet = new Set(ALLOWED_MODELS.map((s) => s.toLowerCase()));
      const aliasMap = {
        'openai.gpt-6-luna': 'gpt-6-luna',
        'us.openai.gpt-6-luna': 'gpt-6-luna',
        'global.openai.gpt-6-luna': 'gpt-6-luna',
        'mistral.mistral-7b-instruct-v0:2': 'mistral-7b',
        'anthropic.claude-3-5-haiku-20241022-v1:0': 'claude-haiku-5-5',
        'claude-3-5-haiku-20241022': 'claude-haiku-5-5',
        'claude-haiku-3-5': 'claude-haiku-5-5',
      };
      if (d?.models) {
        const flat = d.models.flatMap((g) => g.models || [g]);
        const backendMap = new Map(flat.map((m) => [`${m.id}`.toLowerCase(), m]));
        const normalized = DISPLAY.map((def) => {
          // Check if backend has this id or any alias for it
          let found = backendMap.get(def.id.toLowerCase());
          if (!found) {
            for (const [full, short] of Object.entries(aliasMap)) {
              if (short === def.id && backendMap.has(full)) {
                found = backendMap.get(full);
                break;
              }
            }
          }
          if (found) return { ...found, id: def.id, label: def.label, disabled: false };
          return { ...def, disabled: false };
        });
        setAllModels(normalized);
        const defaultModel = normalized.find((m) => `${m.id}`.toLowerCase() === DEFAULT_MODEL.toLowerCase());
        setSelectedModel((prev) => prev || (defaultModel || normalized[0]).id || '');
      } else {
        setAllModels(DISPLAY.map((m) => ({ ...m, disabled: false })));
        const defaultModel = DISPLAY.find((m) => `${m.id}`.toLowerCase() === DEFAULT_MODEL.toLowerCase());
        setSelectedModel((prev) => prev || (defaultModel || DISPLAY[0]).id || '');
      }
    }).catch(() => {
      const DISPLAY = [
        { id: 'gpt-6-luna', label: 'GPT-6 Luna' },
        { id: 'gpt-4o-mini', label: 'GPT-4o Mini' },
        { id: 'mistral-7b', label: 'Mistral 7B Instruct' },
        { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5' },
        { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
      ];
      setAllModels(DISPLAY.map((m) => ({ ...m, disabled: false })));
    });
  }, [auth?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = useCallback((user, token, firebase_uid = null) => {
    const normalizedUser = user
      ? {
          ...user,
          is_admin: hasAdminAccess(user),
        }
      : user;
    // Firebase-only fallback has no backend JWT — never persist the Google
    // credential as Bearer (it 401s everywhere). Store null token so api.js
    // sends guest/unauthenticated requests until a real backend login happens.
    const isFirebaseOnly = !!firebase_uid || !!normalizedUser?.via_firebase;
    const authData = {
      user: normalizedUser,
      token: isFirebaseOnly ? null : token,
      ...(isFirebaseOnly ? { firebase_uid: firebase_uid || normalizedUser?.email_id || 'firebase', firebase_credential: token } : {}),
    };
    setAuth(authData);
    localStorage.setItem('goodgpt_auth', JSON.stringify(authData));
    if (!normalizedUser?.mobile_number) {
      setShowPhoneModal(true);
    }
    navigate('/chat', { replace: true });
  }, [navigate]);

  const handlePhoneSaved = (updatedUser) => {
    const updated = { ...auth, user: updatedUser };
    setAuth(updated);
    localStorage.setItem('goodgpt_auth', JSON.stringify(updated));
    setShowPhoneModal(false);
  };

  const handleGuest = () => setAuth({ user: { name: 'Guest' }, token: null });

  // Subscription-only mode is a soft gate: the app shell stays fully
  // explorable and the pricing prompt is raised only when the user tries to
  // actually use a feature (send text or voice). See PaywallContext.
  const [paywallOpen, setPaywallOpen] = useState(false);
  const promptPaywall = useCallback(() => setPaywallOpen(true), []);

  const paywallRequired =
    accessChecked && accessMode === 'subscription_only' && !activeSub;

  // A plan landing (purchase, "I have already paid" recheck) closes the prompt
  // and lifts the gate in one move.
  useEffect(() => {
    if (!paywallRequired) setPaywallOpen(false);
  }, [paywallRequired]);

  const paywallValue = useMemo(
    () => ({ required: paywallRequired, prompt: promptPaywall }),
    [paywallRequired, promptPaywall]
  );

  const handleLogout = () => {
    setAuth(null);
    setActiveSub(null);
    localStorage.removeItem('goodgpt_auth');
    navigate('/profile');
  };

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const openProfile = () => {
    setProfileForm({ name: auth.user?.name || '', mobile_number: auth.user?.mobile_number || '' });
    setProfileError('');
    setProfileSuccess('');
    setProfileOpen(true);
    setProfileDropdownOpen(false);
    setNavOpen(false);
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess('');
    try {
      const data = await updateProfile(auth.token, profileForm);
      if (data.success) {
        const updated = { ...auth, user: { ...auth.user, ...data.user } };
        setAuth(updated);
        localStorage.setItem('goodgpt_auth', JSON.stringify(updated));
        setProfileSuccess('Profile saved.');
      } else {
        setProfileError(data.message || 'Failed to save.');
      }
    } catch {
      setProfileError('Failed to save profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  // Async callbacks (e.g. the SSE stream renderers in Chat) hold a stale
  // `onUpdateConversations` for several renders after they were created, so
  // resolving a functional update against the render-time `conversations`
  // silently wiped the whole list back to whatever it was at capture time.
  // A ref keeps the updater anchored to the latest committed value; it is
  // written synchronously so two calls in the same tick compose correctly.
  const conversationsRef = useRef(conversations);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  const handleUpdateConversations = (updated) => {
    const next = typeof updated === 'function' ? updated(conversationsRef.current) : updated;
    conversationsRef.current = next;
    setConversations(next);
    saveConversations(next);
  };

  const startNewChat = () => {
    const conv = newConversation();
    const updated = [conv, ...conversations];
    handleUpdateConversations(updated);
    setActiveConvId(conv.id);
    navigate('/chat');
    setNavOpen(false);
  };

  const deleteConversation = (e, id) => {
    e.stopPropagation();
    const updated = conversations.filter((c) => c.id !== id);
    handleUpdateConversations(updated);
    if (activeConvId === id) setActiveConvId(null);
  };

  const selectConversation = (id) => {
    setActiveConvId(id);
    navigate('/chat');
    setNavOpen(false);
  };

  // Returning users land on a fresh chat composer instead of auto-loading their
  // last conversation into the main screen; history stays browsable in the
  // left panel via the sidebar only.
  const authedRef = useRef(false);
  useEffect(() => {
    const isAuthed = !!auth?.user;
    if (isAuthed && !authedRef.current) setActiveConvId(null);
    authedRef.current = isAuthed;
  }, [auth]);

  // ── Public / unauthenticated routes ─────────────────────────────────────────
  const pathname = location.pathname;

  // The pricing prompt is transient UI, not route state. Any navigation —
  // "View plans & pricing", the topbar Plans chip, or a plain tab switch —
  // dismisses it so it can never sit on top of the page the user just asked
  // to see (which is exactly how the plans page used to look "stuck").
  useEffect(() => { setPaywallOpen(false); }, [pathname]);
  const pathParts = pathname.split('/').filter(Boolean);
  const isNoteRoute = pathParts.length === 2 && !['goodgptadmin', 'modeladmin', 'plans', 'consultants', 'search', 'profile', 'tutor', 'chat', 'courses', 'create-course', 'shop', 'affiliate', 'career-quest', 'presentation', 'email-agent', 'settings', 'connectors', 'wallet', 'privacy', 'terms', 'landingpage', 'lpgold', 'lp', 'memorylandingpage', 'orangelandingpage', 'redlandingpage', 'seo', 'email-contact-graph', 'blog', 'white-paper', 'patent'].includes(pathParts[0]);
  const seo = getSeoConfig(pathname, pathParts, !!auth?.token, showLanding);

  // Standalone public routes that don't need the shell
  const publicRoutes = (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/privacy"             element={<LegalPage initialTab="privacy" />} />
        <Route path="/terms"               element={<LegalPage initialTab="terms" />} />
        <Route path="/landingpage"                element={<LandingPageLight onGetStarted={() => { window.location.href = '/'; }} />} />
        <Route path="/lpgold"                     element={<LandingPageGold onGetStarted={() => { window.location.href = '/'; }} />} />
        <Route path="/lp"                         element={<LandingPage onGetStarted={() => { setLoginMode('login'); setShowLanding(false); }} />} />
        <Route path="/lpvirtualassistantimage"    element={<NewLandingPage onGetStarted={() => { window.location.href = '/'; }} />} />
        <Route path="/blog"                       element={<BlogIndexPage />} />
        <Route path="/blog/:slug"                 element={<BlogPostPage />} />
        <Route path="/orangelandingpage"          element={<LandingPageOrange onGetStarted={() => { window.location.href = '/'; }} />} />
        <Route path="/redlandingpage"             element={<LandingPageMain onGetStarted={() => { window.location.href = '/'; }} />} />
        <Route path="/memorylandingpage"   element={<HomePage onGetStarted={() => { window.location.href = '/'; }} />} />
        <Route path="/consultants"         element={<ConsultantPortal />} />
        <Route path="/plans"               element={
          <div className="app" style={{ flexDirection: 'column', overflowY: 'auto' }}>
            <header className="plans-route-nav">
              <a className="plans-route-logo" href="/">
                <span className="app-nav-logo-icon">🔆</span>
                <span className="app-nav-logo-text">Good<span>GPT</span></span>
              </a>
              {auth?.token ? (
                <button className="plans-route-nav-btn" onClick={() => { window.location.href = '/'; }}>← Back to app</button>
              ) : (
                <button className="plans-route-nav-btn" onClick={() => { window.location.href = '/'; }}>Sign in</button>
              )}
            </header>
            <PlansPage token={auth?.token || null} onShowLogin={() => { window.location.href = '/'; }} onOpenWallet={() => setWalletOpen(true)} />
          </div>
        } />
        <Route path="/goodgptadmin/*"      element={
          !auth
            ? <div className="app"><Login onLogin={handleLogin} onGuest={null} /></div>
            : !hasAdminAccess(auth.user)
            ? <div className="app" style={{ display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12, color:'#888' }}>
                <div style={{ fontSize:48 }}>🔒</div>
                <div style={{ fontSize:16, fontWeight:700, color:'#ececec' }}>Access Denied</div>
                <div style={{ fontSize:13 }}>Your account does not have admin privileges.</div>
                <button onClick={handleLogout} style={{ marginTop:8, padding:'8px 20px', background:'#e53935', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600 }}>Sign out</button>
              </div>
            : <GoodGPTAdmin auth={auth} onLogout={handleLogout} />
        } />
        <Route path="/modeladmin/*"        element={
          !auth
            ? <div className="app"><Login onLogin={handleLogin} onGuest={null} /></div>
            : <ModelAdmin auth={auth} onLogout={handleLogout} />
        } />
        <Route path="/:phone/:noteName"    element={<NoteViewRoute />} />
        <Route path="*"                    element={null} />
      </Routes>
    </Suspense>
  );

  // Check if current path is a standalone route (handled above, not the shell)
  const STANDALONE_PATHS = ['/privacy', '/terms', '/landingpage', '/lpgold', '/lp', '/lpvirtualassistantimage', '/blog', '/orangelandingpage', '/redlandingpage', '/memorylandingpage', '/consultants', '/plans', '/goodgptadmin', '/modeladmin'];
  const isStandalone = STANDALONE_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));

  // Note route: /{phone}/{noteName}
  if (isStandalone || isNoteRoute) return <>
    <Seo
      title={seo.title}
      description={seo.description}
      canonical={seo.canonical}
      noindex={!!seo.noindex}
      keywords={seo.keywords}
      jsonLd={seo.jsonLd}
      ogType={seo.ogType}
      ogImageAlt={seo.ogImageAlt}
    />
    {publicRoutes}
  </>;

  // ── Unauthenticated shell ────────────────────────────────────────────────────
  if (!auth && showLanding) {
    return <>
      <Seo
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        noindex={!!seo.noindex}
        keywords={seo.keywords}
        jsonLd={seo.jsonLd}
        ogType={seo.ogType}
        ogImageAlt={seo.ogImageAlt}
      />
      <LandingPageWhiteOrange onGetStarted={() => { setLoginMode('login'); setShowLanding(false); }} />
    </>;
  }

  if (!auth) {
    return (
      <>
        <Seo
          title={seo.title}
          description={seo.description}
          canonical={seo.canonical}
          noindex={!!seo.noindex}
          keywords={seo.keywords}
          jsonLd={seo.jsonLd}
          ogType={seo.ogType}
          ogImageAlt={seo.ogImageAlt}
        />
        <div className="app">
          <Login onLogin={handleLogin} onGuest={handleGuest} initialMode={loginMode} />
        </div>
      </>
    );
  }

  // ── Authenticated shell ──────────────────────────────────────────────────────
  const activeTab = TAB_BY_PATH[pathname]?.key || (pathname === '/settings' ? 'settings' : pathname === '/connectors' ? 'connectors' : 'chat');
  const activeTabMeta = ALL_TABS.find((t) => t.path === pathname);
  const userInitials = (auth.user?.name || auth.user?.email_id || 'G').charAt(0).toUpperCase();

  return (
    <PaywallProvider value={paywallValue}>
      <Seo
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        noindex={!!seo.noindex}
        keywords={seo.keywords}
        jsonLd={seo.jsonLd}
        ogType={seo.ogType}
        ogImageAlt={seo.ogImageAlt}
      />
      <div className="app app-authed">
      {navOpen && <div className="nav-overlay" onClick={() => setNavOpen(false)} />}

      {/* ── Profile edit modal ── */}
      {profileOpen && (
        <div className="profile-modal-overlay" onClick={() => setProfileOpen(false)}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <span className="profile-modal-title">Edit Profile</span>
              <button className="profile-modal-close" onClick={() => setProfileOpen(false)}>×</button>
            </div>
            <div className="profile-modal-user">
              <div className="profile-modal-avatar">{userInitials}</div>
              <div className="profile-modal-username">{auth.user?.name || 'Guest'}</div>
              {auth.user?.email_id && <div className="profile-modal-useremail">{auth.user.email_id}</div>}
            </div>
            <div className="profile-modal-body">
              <div className="profile-field">
                <label>Name</label>
                <input
                  value={profileForm.name}
                  onChange={(e) => setProfileForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Your name"
                />
              </div>
              <div className="profile-field">
                <label>Email</label>
                <input value={auth.user?.email_id || ''} disabled className="profile-input-disabled" />
              </div>
              <div className="profile-field">
                <label>Mobile number</label>
                <input
                  value={profileForm.mobile_number}
                  onChange={(e) => setProfileForm((f) => ({ ...f, mobile_number: e.target.value }))}
                  placeholder="+1234567890"
                />
              </div>
              {profileError   && <div className="profile-error">{profileError}</div>}
              {profileSuccess && <div className="profile-success">{profileSuccess}</div>}
            </div>
            <div className="profile-modal-footer">
              <button className="profile-save-btn" onClick={handleSaveProfile} disabled={profileSaving}>
                {profileSaving ? 'Saving…' : 'Save changes'}
              </button>
              <button className="profile-cancel-btn" onClick={() => setProfileOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Unified left nav ── */}
      <nav className={`app-nav ${navOpen ? 'open' : ''} ${navCollapsed ? 'collapsed' : ''}`}>
        <div className="app-nav-logo">
          <Link to="/profile" className="app-nav-logo-link" onClick={() => setNavOpen(false)}>
            <span className="app-nav-logo-icon">🔆</span>
            <span className="app-nav-logo-text">Good<span>GPT</span></span>
          </Link>
          <button className="nav-collapse-btn" onClick={() => setNavCollapsed(c => !c)} title={navCollapsed ? 'Expand' : 'Collapse'}>
            {navCollapsed ? '›' : '‹'}
          </button>
          <button className="nav-close-btn" onClick={() => setNavOpen(false)}>×</button>
        </div>

        {/* Main tabs */}
        <div className="app-nav-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`app-nav-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => { navigate(t.path); setNavOpen(false); }}
            >
              <span className="app-nav-tab-icon">{t.icon}</span>
              <span className="app-nav-tab-label">{t.label}</span>
            </button>
          ))}
          {PUBLISHING_TABS.map((t) => (
            <button
              key={t.key}
              className={`app-nav-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => { navigate(t.path); setNavOpen(false); }}
            >
              <span className="app-nav-tab-icon">{t.icon}</span>
              <span className="app-nav-tab-label">{t.label}</span>
            </button>
          ))}
          {hasAdminAccess(auth.user) && (
            <a className="app-nav-tab" href="/goodgptadmin">
              <span className="app-nav-tab-icon">⚙️</span>
              <span className="app-nav-tab-label">Admin Panel</span>
            </a>
          )}
        </div>

        {/* AI Model selector — locked to Amazon Nova Canvas for image generation */}
        {allModels.length > 0 && (
          <div className="app-nav-model">
            <div className="app-nav-section-label">AI Model</div>
            <select
              className="app-nav-model-select"
              value={imageGenLocked ? NOVA_CANVAS_MODEL : selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={imageGenLocked}
              title={imageGenLocked ? `${NOVA_CANVAS_LABEL} is fixed for image generation` : undefined}
            >
              {imageGenLocked ? (
                <option value={NOVA_CANVAS_MODEL}>{NOVA_CANVAS_LABEL}</option>
              ) : (
                allModels.map((m) => (
                  <option
                    key={m.id}
                    value={m.id}
                    disabled={!!m.disabled}
                    className={m.disabled ? 'model-option-disabled' : undefined}
                    title={m.disabled ? `${m.label || m.id} — coming soon` : undefined}
                  >
                    {m.label || m.id}{m.disabled ? ' — coming soon' : ''}
                  </option>
                ))
              )}
            </select>
            {imageGenLocked && (
              <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>Locked for image generation</div>
            )}
          </div>
        )}

        {/* Chat history */}
        <div className="app-nav-chats">
          <div className="app-nav-chats-header">
            <span className="app-nav-section-label">Chats</span>
            <button className="new-chat-btn" onClick={startNewChat} title="New chat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>
          <div className="app-nav-chat-list">
            {conversations.length === 0 && (
              <div className="app-nav-chat-empty">No chats yet</div>
            )}
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={`app-nav-chat-item ${conv.id === activeConvId && pathname === '/chat' ? 'active' : ''}`}
                onClick={() => selectConversation(conv.id)}
              >
                <span className="app-nav-chat-title">{conv.title}</span>
                <button
                  className="app-nav-chat-delete"
                  onClick={(e) => deleteConversation(e, conv.id)}
                  title="Delete"
                >×</button>
              </div>
            ))}
          </div>
        </div>

        {/* Legal links */}
        <div className="app-nav-legal">
          <button className="legal-link" onClick={() => setLegalTab('terms')}>Terms</button>
          <span className="app-nav-legal-sep">·</span>
          <button className="legal-link" onClick={() => setLegalTab('privacy')}>Privacy Policy</button>
        </div>

        <div className="app-nav-footer" />
      </nav>

      {/* ── Main content ── */}
      <main className="app-content">
        {/* Free-preview notice: exploring is open to everyone, the pricing
            prompt only fires when a feature is actually used. */}
        {paywallRequired && (
          <div className="explore-banner">
            <span className="explore-banner-icon">🔓</span>
            <span className="explore-banner-text">
              You&rsquo;re exploring for free — a plan is required to send messages or use voice.
            </span>
            <button className="explore-banner-btn" onClick={() => navigate('/plans-page')}>
              View plans
            </button>
          </div>
        )}
        {/* Top bar — the hamburger opens the left nav as an overlay drawer on
            narrow viewports, in embedded WebViews included, so the shell never
            depends on the native side providing navigation. */}
        <div className="app-topbar">
          <button className="app-hamburger" onClick={() => setNavOpen(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <span className="app-topbar-title">
            {pathname === '/settings'
              ? '⚙️ Settings'
              : pathname === '/seo'
              ? '🚀 SEO Agent'
              : `${activeTabMeta?.icon || ''} ${activeTabMeta?.label || ''}`}
          </span>
          <div className="app-topbar-right">
            <button className="wallet-topbar-chip" onClick={() => setWalletOpen(true)} title="My Wallet">
              <span className="wallet-chip-icon">💳</span>
              <span className="wallet-chip-balance">
                {walletLoading ? '…' : `$${(wallet.balance ?? 0).toFixed(2)}`}
              </span>
            </button>
            <button className="plans-topbar-chip" onClick={() => navigate('/plans-page')} title="Subscription">
              🔓 Plans
            </button>
            <div className="profile-topbar-wrap">
              <button
                className="profile-topbar-avatar-btn"
                onClick={() => setProfileDropdownOpen((o) => !o)}
                title="Profile"
              >
                <div className="app-nav-avatar">{userInitials}</div>
                {activeSub && <span className="profile-avatar-pro-dot" title={activeSub.plan_label} />}
              </button>
              {profileDropdownOpen && (
                <div className="profile-topbar-dropdown">
                  <div className="profile-dropdown-user">
                    <div className="profile-dropdown-name">{auth.user?.name || 'Guest'}</div>
                    <div className="profile-dropdown-email">{auth.user?.email_id || ''}</div>
                    {activeSub && (
                      <div className="profile-dropdown-plan">✦ {activeSub.plan_label}</div>
                    )}
                  </div>
                  <button className="profile-dropdown-item" onClick={openProfile}>
                    ✏️ Edit Profile
                  </button>
                  <button className="profile-dropdown-item" onClick={() => { setProfileDropdownOpen(false); navigate('/profile'); }}>
                    👤 Profile
                  </button>
                  <button className="profile-dropdown-item" onClick={() => { setProfileDropdownOpen(false); setReferOpen(true); }}>
                    🔗 Referral
                  </button>
                  <button className="profile-dropdown-item" onClick={() => { setProfileDropdownOpen(false); navigate('/profile'); }}>
                    🧠 Active Memory ({memoryCount})
                  </button>
                  <button className="profile-dropdown-item" onClick={() => { setProfileDropdownOpen(false); navigate('/affiliate'); }}>
                    🤝 Affiliate
                  </button>
                  <button className="profile-dropdown-item" onClick={() => { setProfileDropdownOpen(false); navigate('/plans-page'); }}>
                    🔓 Subscription
                  </button>
                  <button className="profile-dropdown-item" onClick={() => { setProfileDropdownOpen(false); navigate('/settings'); }}>
                    ⚙️ Settings
                  </button>
                  <button className="profile-dropdown-item profile-dropdown-logout" onClick={() => { setProfileDropdownOpen(false); handleLogout(); }}>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Tab content via routes ── */}
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/"              element={<Navigate to="/chat" replace />} />
          <Route path="/search"        element={<LiteratureSearch token={auth.token} activeSub={activeSub} />} />
          <Route path="/profile"       element={
            <Profile
              user={auth.user}
              token={auth.token}
              conversations={conversations}
              memoryEnabled={memoryEnabled}
              skipDatabaseSave={skipDatabaseSave}
              onMemoryCountChange={setMemoryCount}
              onNavigate={(key) => {
                if (key?.tab) { setActiveConvId(key.convId); navigate('/' + key.tab.replace('_', '-')); return; }
                navigate('/' + key.replace('_', '-'));
              }}
            />
          } />
          <Route path="/tutor"         element={<AITutor user={auth.user} token={auth.token} model={selectedModel} memoryEnabled={memoryEnabled} skipDatabaseSave={skipDatabaseSave} />} />
          <Route path="/chat"          element={
            <Chat
              user={auth.user}
              token={auth.token}
              model={imageGenLocked ? NOVA_CANVAS_MODEL : selectedModel}
              conversations={conversations}
              activeId={activeConvId}
              onSetActiveId={setActiveConvId}
              onUpdateConversations={handleUpdateConversations}
              skipDatabaseSave={skipDatabaseSave}
              memoryEnabled={memoryEnabled}
              onMemorySaved={() => {
                const email = auth.user?.email_id;
                if (email) getMemorySummary(email).then((d) => { if (d?.success && typeof d.memory_count === 'number') setMemoryCount(d.memory_count); }).catch(() => {});
              }}
              onImageGenLockedChange={handleImageGenLockedChange}
            />
          } />
          {/* <Route path="/coach"         element={<ExecutiveCoach token={auth.token} />} /> */}
          <Route path="/courses"       element={<Courses user={auth.user} token={auth.token} />} />
          <Route path="/create-course" element={<Courses user={auth.user} token={auth.token} initialView="create" />} />
          <Route path="/shop"          element={<Shop user={auth.user} token={auth.token} wallet={wallet} onPurchase={handleShopPurchase} onGoToWallet={() => setWalletOpen(true)} />} />
          <Route path="/affiliate"     element={<Affiliate user={auth.user} token={auth.token} />} />
          <Route path="/assets"         element={<Assets user={auth.user} token={auth.token} conversations={conversations} />} />
          <Route path="/progress"       element={<Navigate to="/assets" replace />} />
          <Route path="/career-quest"  element={<div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}><CareerQuest user={auth.user} token={auth.token} model={selectedModel} memoryEnabled={memoryEnabled} skipDatabaseSave={skipDatabaseSave} /></div>} />
          <Route path="/presentation"  element={<Presentation user={auth.user} token={auth.token} model={selectedModel} />} />
          <Route path="/email-agent"          element={<Navigate to="/connectors" replace />} />
          <Route path="/publish"       element={<PublishHub token={auth.token} activeSub={activeSub} />} />
          <Route path="/white-paper"   element={<Navigate to="/publish" replace />} />
          <Route path="/publish-book"  element={<Navigate to="/publish" replace />} />
          <Route path="/patent"        element={<Navigate to="/publish" replace />} />
          <Route path="/connectors"           element={<Connectors user={auth.user} token={auth.token} />} />
          <Route path="/plans-page"    element={<PlansPage token={auth.token} onShowLogin={handleLogout} onOpenWallet={() => setWalletOpen(true)} />} />
          <Route path="/seo"           element={<SEOAgent user={auth.user} token={auth.token} model={selectedModel} />} />
          <Route path="/settings"      element={
            <Settings
              memoryEnabled={memoryEnabled}
              onMemoryToggle={handleMemoryToggle}
              skipDatabaseSave={skipDatabaseSave}
              onPrivateModeToggle={handlePrivateModeToggle}
              bringYourOwnModelForm={bringYourOwnModelForm}
              onBringYourOwnModelChange={setBringYourOwnModelForm}
              onSaveBringYourOwnModel={handleSaveBringYourOwnModel}
              bringYourOwnModelSaving={bringYourOwnModelSaving}
              bringYourOwnModelError={bringYourOwnModelError}
              bringYourOwnModelSuccess={bringYourOwnModelSuccess}
            />
          } />
          <Route path="*"              element={<Navigate to="/chat" replace />} />
          </Routes>
        </Suspense>
      </main>

      {/* ── Bottom nav (mobile browsers only — hidden in embedded WebViews) ── */}
      {!embedded && (
      <div className="app-bottom-nav">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`app-bottom-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => navigate(t.path)}
          >
            <span className="app-bottom-tab-icon">{t.icon}</span>
            <span className="app-bottom-tab-label">{t.label}</span>
          </button>
        ))}
      </div>
      )}

      {legalTab && <Legal initialTab={legalTab} onClose={() => setLegalTab(null)} />}

      {showPhoneModal && (
        <PhonePromptModal
          auth={auth}
          onSaved={handlePhoneSaved}
          onSkip={() => setShowPhoneModal(false)}
        />
      )}

      {referOpen && (
        <div className="wallet-panel-overlay" onClick={() => setReferOpen(false)}>
          <div className="wallet-panel-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-panel-header">
              <span className="wallet-panel-title">🔗 Refer & Earn</span>
              <button className="cart-close-btn" onClick={() => setReferOpen(false)}>×</button>
            </div>
            <div className="wallet-panel-body">
              <ReferEarn token={auth.token} />
            </div>
          </div>
        </div>
      )}

      {!embedded && (
      <button
        className={`integrations-fab ${pathname === '/connectors' ? 'active' : ''}`}
        onClick={() => navigate('/connectors')}
        title="Integrations"
      >
        <span className="integrations-fab-icon">🔌</span>
        <span className="integrations-fab-label">Integrations</span>
      </button>
      )}

      {/* WhatsApp support — icon-only FAB, bottom right (was a topbar chip) */}
      <a
        className="whatsapp-fab"
        href={whatsappLink('Hi GoodGPT support — I have a question about your plans.')}
        target="_blank"
        rel="noopener noreferrer"
        title="Chat with us on WhatsApp"
        aria-label="Chat with us on WhatsApp"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {walletOpen && (
        <div className="wallet-panel-overlay" onClick={() => setWalletOpen(false)}>
          <div className="wallet-panel-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-panel-header">
              <span className="wallet-panel-title">💳 Wallet</span>
              <button className="cart-close-btn" onClick={() => setWalletOpen(false)}>×</button>
            </div>
            <div className="wallet-panel-body">
              <Wallet
                user={auth.user}
                token={auth.token}
                wallet={wallet}
                onRefresh={() => refreshWallet(auth.token)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Pricing prompt — raised only when a gated feature is used, never on
          browsing. Dismissing it returns the user to exploring. */}
      {paywallOpen && (
        <AccessGate
          auth={auth}
          trial={accessTrial}
          onSignOut={handleLogout}
          onClose={() => setPaywallOpen(false)}
          onAccessChanged={(d) => {
            setActiveSub(d.active_subscription || null);
            setAccessMode(d.access_mode || null);
            setAccessTrial(d.trial || null);
          }}
        />
      )}
      </div>
    </PaywallProvider>
  );
}

// Thin wrapper so NoteView can read route params
function NoteViewRoute() {
  const parts = useLocation().pathname.split('/').filter(Boolean);
  return <NoteView phone={parts[0]} noteName={decodeURIComponent(parts[1] || '')} />;
}
